/**
 * The rival's voice: race events in, one line of trash talk out.
 *
 * Kept apart from RivalBrain because the two want opposite things. The
 * brain is a planner on a latency budget, silent on routed levels because
 * the route already answers "where next". The voice has no deadline (a
 * line landing a second late still lands), runs on a model chosen for
 * writing, and matters most exactly when the brain is quiet.
 *
 * Three rules keep it from becoming noise:
 * - Events are earned, not timed: taking the lead, losing it, a death, the
 *   last shard, the final stretch. No idle chatter.
 * - A cooldown and a per-race cap sit above that, so a scrappy race in a
 *   tight sector cannot produce a wall of text.
 * - Every event has a canned line. Offline, rate limited, unconfigured,
 *   agent mode: the rival still talks, it just repeats itself. Silence is
 *   the one outcome that is never acceptable.
 */
const BANK = {
  race_start: ['Try to keep up.', 'Watch the line I take.', 'Same gate, different class.'],
  took_lead: ['And there it goes.', 'Front from here.', 'Told you.'],
  lost_lead: ['Enjoy it while it lasts.', 'Borrowed, not kept.', 'Cute.'],
  player_died: ['The void does not negotiate.', 'That looked expensive.', 'Take your time.'],
  rival_died: ['Rehearsal.', 'Noted. Adjusting.', 'That one is on me.'],
  shard: ['Collected.', 'One less for you.', 'Shiny.'],
  near_finish: ['Gate in sight.', 'Closing this out.', 'Last stretch.'],
  rival_won: ['Was that your best?', 'Gate is mine.', 'Good race. Mine, though.'],
  player_won: ['Fine. That was fast.', 'You earned that one.', 'Rematch.'],
};

export class RivalVoice {
  constructor({ bus, endpoint = '/api/rival/voice' } = {}) {
    this.bus = bus;
    this.endpoint = endpoint;
    this.enabled = true;
    this.available = false;      // set alongside the brain's health probe
    this.cooldown = 11;          // seconds between lines
    this.maxPerRace = 6;
    this.timeoutMs = 9000;

    this._cool = 0;
    this._spoken = 0;
    this._inFlight = false;
    this._bankIx = 0;
    this._seen = new Set();      // once-per-race events
  }

  reset() {
    this._cool = 0;
    this._spoken = 0;
    this._seen.clear();
    this._inFlight = false;
  }

  update(dt) { if (this._cool > 0) this._cool -= dt; }

  /** A canned line, rotated so a repeat event is not a literal repeat. */
  _canned(event) {
    const bank = BANK[event] ?? BANK.race_start;
    return bank[(this._bankIx++) % bank.length];
  }

  /**
   * Report a race event. Speaks immediately from the bank, then upgrades
   * to a written line if one arrives in time: the rival is never silent
   * waiting on a network, and never says two things about one moment.
   */
  say(event, facts = {}, { once = false } = {}) {
    if (!this.enabled) return false;
    if (once && this._seen.has(event)) return false;
    if (this._cool > 0 || this._spoken >= this.maxPerRace) return false;
    this._seen.add(event);
    this._cool = this.cooldown;
    this._spoken++;

    const line = this._canned(event);
    if (!this.available || this._inFlight) {
      this.bus.emit('rival:say', { text: line });
      return true;
    }
    // The written line replaces the canned one only if it beats the
    // player's attention span; otherwise the canned line has already run.
    const stamp = Symbol('line');
    this._pending = stamp;
    this.bus.emit('rival:say', { text: line });
    this._request(event, facts, stamp);
    return true;
  }

  async _request(event, facts, stamp) {
    this._inFlight = true;
    const ctl = new AbortController();
    const timer = setTimeout(() => ctl.abort(), this.timeoutMs);
    try {
      const res = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ event, ...facts }),
        signal: ctl.signal,
      });
      const body = await res.json().catch(() => null);
      if (res.status === 429) { this.available = false; return; }
      if (!res.ok || !body?.ok || !body.say) return;
      // Stale replies are dropped: the moment they described has passed.
      if (this._pending !== stamp) return;
      this.bus.emit('rival:say', { text: String(body.say).slice(0, 120) });
    } catch {
      /* flavour is optional; the canned line already spoke */
    } finally {
      clearTimeout(timer);
      this._inFlight = false;
    }
  }
}
